import React, {useEffect, useState} from 'react';
import {PageTopActions} from '../PageTopActions';
import {useTranslation} from 'react-i18next';
import {Box, Button, Container, Grid} from '@mui/material';
import {Link} from 'react-router-dom';
import PageHeadline from '../../components/PageHeadline';
import {ResponseListWrapper} from '../../services/model/ResponseListWrapper';
import {FeatureName, Path} from '../../../paths';
import {PageStickyHeader} from '../PageStickyHeader';
import {useTableColumns} from '../UseTableColumns';
import DataGridCustom from '../../components/DataGridCustom';
import {TestAttributes} from '../../TestAttributes';
import {LocalPresenceDids} from '../../model/LocalPresenceDids';
import {HttpMethods} from '../../model/HttpMethods';
import {Skeleton} from '@mui/lab';
import {usePermissions, UsePermissionState} from '../UsePermissions';
import SpartanSnackbar from '../../components/SpartanSnackbar';
import {NotificationType} from '../../services/NotificationService';
import AccessControl, {UserPermissions} from '../../components/shared/AccessControl';
import {useAxiosContext} from '../../context/AxiosContext';
import {useLoading} from '../../context/LoadingContext';
import LocalPresenceAttachmentSpeedDial from './components/LocalPresenceAttachmentSpeedDial';

function LocalPresenceDidsListPage() {
  const {t} = useTranslation();
  const {setLoading} = useLoading();
  const {useAxiosOrganizationAPI} = useAxiosContext();
  const [localPresenceDids, setLocalPresenceDids] = useState<Array<LocalPresenceDids>>([]);
  const {userPermissions}: UsePermissionState = usePermissions(FeatureName.LOCAL_PRESENCE_DIDS);

  const tableColumns = useTableColumns({
    featureName: FeatureName.LOCAL_PRESENCE_DIDS,
    defaultActionColumn: true,
  });

  const [{data: entityList, loading: isEntityListLoading, error: hasEntityListError}, getLocalPresenceDIDList] =
    useAxiosOrganizationAPI<ResponseListWrapper<LocalPresenceDids>>(
      {url: `/${Path.LOCAL_PRESENCE_DIDS}`, method: HttpMethods.GET},
      {
        manual: false,
      }
    );

  useEffect(() => {
    if (entityList) {
      setLocalPresenceDids(entityList.results);
    }
  }, [entityList]);

  useEffect(() => {
    setLoading(isEntityListLoading, 'LocalPresenceDidsListPage');
  }, [isEntityListLoading]);

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        minHeight: '100%',
        py: 3,
      }}
    >
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          {hasEntityListError && <SpartanSnackbar type={NotificationType.ERROR} message={hasEntityListError.message} />}
          <PageStickyHeader>
            <Grid container item xs={12} rowSpacing={{xs: 3, sm: 3}}>
              <Grid item xs={12} md={6} sx={{display: 'flex', alignItems: 'center'}}>
                <PageHeadline>{t('local-presence-dids.headline')}</PageHeadline>
              </Grid>
              <Grid item xs={12} md={5} sx={{alignSelf: 'center'}}>
                <PageTopActions>
                  <AccessControl userPermissions={userPermissions} allowedPermissions={[UserPermissions.CREATE]}>
                    <Link to="/local-presence-dids/create">
                      <Button fullWidth color="secondary" id={'add-btn'} {...{[TestAttributes.BUTTON_NAME]: 'add-btn'}}>
                        {t('globalActions.addButton')}
                      </Button>
                    </Link>
                  </AccessControl>
                </PageTopActions>
              </Grid>
              <Grid item xs={6} md={1}>
                <AccessControl userPermissions={userPermissions} allowedPermissions={[UserPermissions.VIEW_UPLOAD_TSV]}>
                  <LocalPresenceAttachmentSpeedDial resourceCallback={getLocalPresenceDIDList} />
                </AccessControl>
              </Grid>
            </Grid>
          </PageStickyHeader>

          <Grid item xs={12} sx={{height: '750px'}} {...{[TestAttributes.TABLE_NAME]: 'table-results'}}>
            {isEntityListLoading ? (
              <Skeleton variant="rectangular" height={500} />
            ) : (
              <DataGridCustom
                tableColumns={tableColumns}
                rows={
                  localPresenceDids?.map((item: LocalPresenceDids, index: number) => {
                    return {
                      ...item,
                      actions: item.bucket_did_number_id,
                      id: index,
                    };
                  }) || []
                }
                userPermissions={userPermissions}
              />
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default LocalPresenceDidsListPage;
